import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';
import { FaSun, FaMoon } from 'react-icons/fa';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const themes = {
  light: {
    backgroundColor: 'bg-white',
    textColor: 'text-gray-800',
    borderColor: 'border-gray-300',
    buttonColor: 'bg-purple-600',
    buttonHoverColor: 'hover:bg-purple-700',
    shadow: 'shadow-md'
  },
  dark: {
    backgroundColor: 'bg-gray-800',
    textColor: 'text-white',
    borderColor: 'border-gray-600',
    buttonColor: 'bg-purple-800',
    buttonHoverColor: 'hover:bg-purple-900',
    shadow: 'shadow-none'
  },
  autumn: {
    backgroundColor: 'bg-orange-100',
    textColor: 'text-gray-800',
    borderColor: 'border-orange-300',
    buttonColor: 'bg-orange-600',
    buttonHoverColor: 'hover:bg-orange-700',
    shadow: 'shadow-md'
  }
};

const DaysUntilFallCalculator = () => {
  const [currentDate, setCurrentDate] = useState(new Date().toISOString().slice(0, 10));
  const [year, setYear] = useState(new Date().getFullYear());
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [theme, setTheme] = useState('light');
  const [chartData, setChartData] = useState(null);
  const [countdownText, setCountdownText] = useState('---');

  useEffect(() => {
    if (result) {
      const interval = setInterval(() => {
        setCountdownText(calculateCountdown(result));
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [result]);

  const calculateCountdown = (days) => {
    const now = new Date();
    const target = new Date(now.getFullYear(), 8, 22); // September 22nd
    if (now > target) {
      target.setFullYear(target.getFullYear() + 1);
    }
    const diff = target - now;

    const daysLeft = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hoursLeft = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutesLeft = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const secondsLeft = Math.floor((diff % (1000 * 60)) / 1000);

    return `${daysLeft}d ${hoursLeft}h ${minutesLeft}m ${secondsLeft}s`;
  };

  useEffect(() => {
    // Load the saved theme from localStorage
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setTheme(savedTheme);
    }
  }, []);

  const handleDateChange = (e) => {
    setCurrentDate(e.target.value);
    setError('');
  };

  const handleYearChange = (e) => {
    setYear(e.target.value);
  };

  const calculateDaysUntilFall = () => {
    try {
      const date = new Date(`${currentDate}T00:00:00Z`);
      const fallStartDate = new Date(year, 8, 22); // September 22nd

      if (date > fallStartDate) {
        fallStartDate.setFullYear(fallStartDate.getFullYear() + 1); // Next year's fall
      }

      const daysUntilFall = Math.ceil((fallStartDate - date) / (1000 * 60 * 60 * 24));
      setResult(daysUntilFall);

      // Generate chart data
      const days = Array.from({ length: daysUntilFall }, (_, i) => i);
      const hours = days.map(day => (daysUntilFall - day) * 24);
      const minutes = hours.map(hour => hour * 60);
      const seconds = minutes.map(min => min * 60);

      setChartData({
        labels: days,
        datasets: [
          {
            label: 'Days',
            data: days,
            borderColor: 'rgba(75,192,192,1)',
            tension: 0.1,
            fill: false
          },
          {
            label: 'Hours',
            data: hours,
            borderColor: 'rgba(153,102,255,1)',
            tension: 0.1,
            fill: false
          },
          {
            label: 'Minutes',
            data: minutes,
            borderColor: 'rgba(255,99,132,1)',
            tension: 0.1,
            fill: false
          },
          {
            label: 'Seconds',
            data: seconds,
            borderColor: 'rgba(255,159,64,1)',
            tension: 0.1,
            fill: false
          }
        ]
      });
    } catch (error) {
      setError('An error occurred while calculating the days until fall');
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : theme === 'dark' ? 'autumn' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme); // Save the new theme to localStorage
  };

  const currentTheme = themes[theme];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className={`min-h-screen ${currentTheme.backgroundColor} ${currentTheme.textColor}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Days Until Fall Calculator</title>
          <meta name="description" content="Calculate the number of days until the start of fall." />
          <meta name="keywords" content="days until fall calculator, fall countdown, autumn start" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8 max-w-2xl mx-auto">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-purple-600 mr-4">🍁</div>
            <h1 className="text-3xl font-bold text-gray-800">Days Until Fall Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The Days Until Fall Calculator helps you determine the number of days remaining until the start of fall.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Current Date:</label>
              <input
                type="date"
                value={currentDate}
                onChange={handleDateChange}
                className={`w-full px-4 py-3 border ${currentTheme.borderColor} rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                placeholder="Enter current date"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Year:</label>
              <input
                type="number"
                value={year}
                onChange={handleYearChange}
                className={`w-full px-4 py-3 border ${currentTheme.borderColor} rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                placeholder="Enter year"
              />
            </div>
          </div>

          <button
            onClick={calculateDaysUntilFall}
            className={`w-full py-3 px-4 ${currentTheme.buttonColor} text-white font-medium rounded-lg ${currentTheme.buttonHoverColor} focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors duration-200`}
          >
            Calculate Days Until Fall
          </button>

          <button
            onClick={toggleTheme}
            className="mt-4 py-2 px-4 bg-gray-200 text-gray-800 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
          >
            {theme === 'light' ? <FaSun /> : theme === 'dark' ? <FaMoon /> : 'Autumn Theme'}
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {result !== null && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Days Until Fall</h2>
              
              <div className={`bg-gray-50 p-6 rounded-lg ${currentTheme.shadow}`}>
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Result</h3>
                <p className="text-xl font-bold">{result} days</p>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Countdown Timer</h3>
                <div className="text-2xl font-bold text-center">
                  {countdownText}
                </div>
              </div>

              {chartData && (
                <div className={`bg-white p-6 rounded-lg shadow-md mt-8 ${currentTheme.shadow}`}>
                  <Line
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Time Remaining'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Days'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Countdown to Fall'
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* How to Use Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Days Until Fall Calculator <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Days Until Fall Calculator effectively:</p>
            
            <div className="step-section mt-4">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Enter Current Date</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Select the current date using the date picker.</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Select Year</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Select the year for which you want to calculate the days until fall.</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Calculate</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Click the "Calculate Days Until Fall" button to get the results.</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 4: Review Results</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Check the number of days remaining until the start of fall.</li>
                <li>View the countdown timer to see the remaining time in real-time.</li>
                <li>Visualize the countdown with the graph.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ <span className="text-blue-600">❓</span></h2>
          <div className="text-gray-600">
            <div className="faq-item mb-4">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">When does fall start?</h3>
              <p>Fall typically starts around September 22nd in the Northern Hemisphere.</p>
            </div>

            <div className="faq-item mb-4">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Why does the start date of fall vary?</h3>
              <p>The exact start date of fall can vary slightly due to the Earth's orbit and the Gregorian calendar. It usually falls between September 21st and 24th.</p>
            </div>

            <div className="faq-item mb-4">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Can I use this calculator for other seasons?</h3>
              <p>No, this calculator is specifically designed to count the days until the start of fall. For other seasons, you would need a different calculator.</p>
            </div>
          </div>
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📈</span></h2>
          <div className="text-gray-600">
            <p>Let's say today is August 15th, 2023.</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Select August 15th in the date picker.</li>
              <li>Select the year 2023.</li>
              <li>Click the "Calculate Days Until Fall" button.</li>
              <li>The calculator will show the number of days remaining until September 22nd, 2023.</li>
              <li>The countdown timer will display the remaining time in real-time.</li>
              <li>The graph will visualize the countdown in days, hours, minutes, and seconds.</li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DaysUntilFallCalculator;